// ORIGINAL EVIDENCE-BASED SCORING ALGORITHM
// Sums all "years" adjustments and adds to chronological age to get True Health Age

import { QUESTION_METADATA } from './questionMetadata'

// ─────────────────────────────────────────────────────────────────────────
// Score compression + "Reversible Years" (added 2026-06-03 per Keith review)
//
// PROBLEM this solves: the original formula was `chronoAge + sum(penalties)`
// with the protective side capped at -5 but the aging side UNCAPPED. An
// 80-year-old with several real conditions stacked +35 and saw "Health Age
// 115" — clinically absurd (she has already survived to 80) and
// motivationally fatal. There was also no age scaling and no sense of how
// much of the gap is actually reversible.
//
// FIX:
//   1. Compress the positive (aging) gap through a saturating curve that is
//      ~linear at the low end (so healthy/moderate users keep an honest,
//      near-unchanged number) but asymptotes to AGING_SOFTCAP_MAX so no one
//      ever sees a crushing gap. The protective -5 cap is unchanged.
//   2. Compute REVERSIBLE YEARS — the share of the (compressed) gap that
//      comes from modifiable factors (diet, movement, sleep, stress,
//      smoking, alcohol, BMI, controlled BP/chol, prediabetes/T2). Fixed
//      history (cancer, prior MI/stroke/stent/bypass, type-1 diabetes) is
//      excluded. This is the honest, motivating number: "X of your gap is
//      reversible — here's how to claim it back."
//
// Reversible years are always ≤ the displayed gap, so the math stays
// coherent on the results page.
// ─────────────────────────────────────────────────────────────────────────
const AGING_SOFTCAP_MAX = 15  // hard ceiling on how many years "older" we ever show
const AGING_SOFTCAP_K = 12    // curve steepness; larger = more linear at low end
const ANTI_AGING_CAP = -5     // protective effect cap (unchanged from prior review)

// Questions whose worst answers represent FIXED history that lifestyle can't
// reverse. Used to split the gap into reversible vs. fixed.
const FIXED_QIDS = new Set([24]) // cancer history is always fixed

function isModifiableFactor(qId, answerText = '') {
  if (FIXED_QIDS.has(qId)) return false
  // Cardiovascular: established events (Q23) are fixed history; controlled
  // BP/cholesterol is modifiable.
  if (qId === 23 && /MI|stroke|stent|bypass|heart attack/i.test(answerText)) return false
  // Type-1 diabetes is autoimmune, not lifestyle-reversible; pre/T2 are.
  if (qId === 5 && /type\s*1|\bt1\b/i.test(answerText)) return false
  return true
}

export function calculatePhase1Results(answers) {
  // Get user's chronological age (now in question ID 1)
  const chronoAge = parseInt(answers[1]?.text) || 40

  // Define question categories — must match the question IDs declared in
  // Phase1Quiz.jsx exactly. Movement has 3 questions (10, 11, 12 — exercise,
  // sitting, fitness). Sleep is 13-15 (duration, quality, apnea). Earlier
  // versions had this off-by-one starting at Q12, which caused the fitness
  // answer to be grouped under Sleep and the metadata strings to be pulled
  // from the wrong question (the "untreated sleep apnea" text on a
  // non-apnea Sleep result was the visible symptom).
  const categoryMap = {
    'Baseline': [1, 2],
    'High-Impact Risks': [3, 4, 5, 23, 24],
    'Body & Vitals': [6, 7, 8, 9, 25],
    'Movement': [10, 11, 12],
    'Sleep': [13, 14, 15],
    'Nutrition': [16, 17, 18],
    'Mental Health': [19, 20, 26, 27],
  }

  // Sum all years adjustments from answers
  let yearsAdjustment = 0
  const factors = []
  const categoryYears = {}

  // Initialize category years
  Object.keys(categoryMap).forEach(cat => {
    categoryYears[cat] = 0
  })

  Object.entries(answers).forEach(([questionId, answerData]) => {
    const qId = parseInt(questionId)

    if (qId === 1 || qId === 2) {
      // Skip age and sex - just metadata
      return
    }

    // Find which category this question belongs to
    const category = Object.entries(categoryMap).find(([_, qIds]) => qIds.includes(qId))?.[0] || 'Other'

    // Handle multi-select (life events)
    if (answerData.selections) {
      const totalYears = answerData.selections.reduce((sum, sel) => sum + sel.years, 0)
      yearsAdjustment += totalYears
      categoryYears[category] += totalYears
      answerData.selections.forEach(sel => {
        factors.push({
          questionId: qId,
          answer: sel.text,
          years: sel.years,
          category,
          type: sel.years > 0 ? 'aging' : 'protecting',
          modifiable: isModifiableFactor(qId, sel.text)
        })
      })
    } else {
      // Single answer
      yearsAdjustment += answerData.years
      categoryYears[category] += answerData.years
      factors.push({
        questionId: qId,
        answer: answerData.text,
        years: answerData.years,
        category,
        type: answerData.years > 0 ? 'aging' : 'protecting',
        modifiable: isModifiableFactor(qId, answerData.text)
      })
    }
  })

  // ── Compress the aging gap + compute reversible share ──────────────────
  // Raw sums first (uncompressed), then a saturating curve so heavy disease
  // burdens never produce an absurd number while healthy/moderate users keep
  // an essentially unchanged, honest gap.
  const rawPositive = factors.filter(f => f.years > 0).reduce((s, f) => s + f.years, 0)
  const rawProtective = factors.filter(f => f.years < 0).reduce((s, f) => s + f.years, 0)
  const rawModifiablePositive = factors
    .filter(f => f.years > 0 && f.modifiable)
    .reduce((s, f) => s + f.years, 0)

  // Saturating compression: ~linear near 0, asymptotes to AGING_SOFTCAP_MAX.
  const displayedPositive = rawPositive > 0
    ? AGING_SOFTCAP_MAX * (1 - Math.exp(-rawPositive / AGING_SOFTCAP_K))
    : 0
  // Ratio used to keep per-factor / per-category reversible numbers coherent
  // with the (compressed) headline gap.
  const compressionRatio = rawPositive > 0 ? displayedPositive / rawPositive : 0

  // Calculate category scores and health status (exclude Baseline from reporting)
  const categoryScores = {}
  const categoryStatus = {}
  const categoryExplanations = {}

  Object.entries(categoryMap).forEach(([category, qIds]) => {
    // Skip Baseline - it's metadata only
    if (category === 'Baseline') {
      return
    }

    const categoryFactors = factors.filter(f => f.category === category)
    const totalYears = categoryYears[category]

    // How many of the (compressed) gap years this category contributes are
    // reversible — i.e. come from modifiable factors. Drives the
    // "Lower your TrueHealth Age by ~X years" protocol cards.
    const categoryModifiablePositive = categoryFactors
      .filter(f => f.years > 0 && f.modifiable)
      .reduce((s, f) => s + f.years, 0)
    const categoryReversibleYears = Math.round(categoryModifiablePositive * compressionRatio)

    // Count questions with positive years in this category
    const positiveYearsFactors = categoryFactors.filter(f => f.years > 0)
    const numQuestionsInCategory = qIds.filter(qId => qId > 2).length // Exclude baseline
    const percentBadQuestions = numQuestionsInCategory > 0 ? (positiveYearsFactors.length / numQuestionsInCategory) : 0

    // Find if there's a single very bad question (>= 5 years)
    const veryBadQuestion = categoryFactors.find(f => f.years >= 5)

    // "Why It Matters" must come from the question actually driving the score —
    // not the first question in the category. Otherwise a non-smoker who drinks
    // heavily would see "smoking is the strongest predictor..." paired with
    // alcohol-reduction steps, which is incoherent.
    let primaryFactor = null
    if (totalYears > 0) {
      // Aging-direction: pick the worst contributor
      primaryFactor = [...categoryFactors]
        .filter(f => f.years > 0)
        .sort((a, b) => b.years - a.years)[0]
    } else if (totalYears < 0) {
      // Protecting-direction: pick the most-protecting factor
      primaryFactor = [...categoryFactors]
        .filter(f => f.years < 0)
        .sort((a, b) => a.years - b.years)[0]
    }
    // Fallback only when nothing contributed in either direction
    const primaryQId = primaryFactor?.questionId ?? qIds.find(qId => qId > 2)
    const categoryMetadata = QUESTION_METADATA[primaryQId]
    const whyItMatters = categoryMetadata?.whyItMatters || ''

    // Get improvement steps if there are concerns
    let improvementSteps = []
    if (totalYears > 0) {
      categoryFactors.forEach(factor => {
        const metadata = QUESTION_METADATA[factor.questionId]
        if (metadata?.improvementSteps && factor.years > 0) {
          improvementSteps.push(metadata.improvementSteps)
        }
      })
    }

    // Determine status
    let status
    if (totalYears < 0) {
      status = 'Great'
      categoryScores[category] = { years: totalYears, status, color: '#4CAF50', whyItMatters }
    } else if (totalYears === 0) {
      status = 'Good'
      categoryScores[category] = { years: totalYears, status, color: '#81C784', whyItMatters }
    } else if (veryBadQuestion || percentBadQuestions > 0.5) {
      status = 'Serious Concern'
      categoryScores[category] = { years: totalYears, status, color: '#F44336', whyItMatters, improvementSteps: improvementSteps.slice(0, 2) }
    } else if (totalYears > 0) {
      status = 'Moderate Concern'
      categoryScores[category] = { years: totalYears, status, color: '#FFC107', whyItMatters, improvementSteps: improvementSteps.slice(0, 1) }
    }

    if (categoryScores[category]) {
      categoryScores[category].reversibleYears = categoryReversibleYears
    }

    categoryStatus[category] = status
  })

  // Protective effect cap (unchanged from 2026-05-15 review): users can't
  // score more than 5 years biologically younger without biomarker
  // validation. The aging side is no longer uncapped — it's compressed above.
  const cappedProtective = Math.max(rawProtective, ANTI_AGING_CAP)

  // Net gap = compressed aging burden minus capped protective effect.
  const netGap = displayedPositive + cappedProtective

  // Calculate True Health Age + age difference from the compressed gap.
  const trueHealthAge = Math.round(chronoAge + netGap)
  const ageDiff = Math.round(netGap)

  // REVERSIBLE YEARS — the share of the displayed gap that comes from
  // modifiable factors. This is the motivating headline for every user,
  // especially older/sicker ones: most of their gap can be clawed back.
  const reversibleYears = rawPositive > 0
    ? Math.round(displayedPositive * (rawModifiablePositive / rawPositive))
    : 0
  // The remainder of the positive gap is fixed history (informational only).
  const fixedYears = Math.max(0, Math.round(displayedPositive) - reversibleYears)

  // Determine grade based on age difference
  let grade, label
  if (ageDiff <= -5) {
    grade = 'A+'
    label = 'Exceptional'
  } else if (ageDiff <= -2) {
    grade = 'A'
    label = 'Excellent'
  } else if (ageDiff <= 2) {
    grade = 'B'
    label = 'Good'
  } else if (ageDiff <= 5) {
    grade = 'C'
    label = 'Fair'
  } else {
    grade = 'D'
    label = 'Needs Improvement'
  }

  // Get top 3 aging factors (positive years)
  const agingFactors = factors
    .filter(f => f.years > 0)
    .sort((a, b) => b.years - a.years)
    .slice(0, 3)

  // Get top 3 protecting factors (negative years)
  const protectingFactors = factors
    .filter(f => f.years < 0)
    .sort((a, b) => a.years - b.years)
    .slice(0, 3)

  const top3Aging = agingFactors.map(f => ({
    answer: f.answer,
    years: f.years
  }))

  const top3Protecting = protectingFactors.map(f => ({
    answer: f.answer,
    years: Math.abs(f.years)
  }))

  return {
    trueHealthAge,
    chronoAge,
    ageDiff,
    grade,
    label,
    yearsAdjustment,
    // Reversible-years model (2026-06-03). reversibleYears is the achievable
    // reduction from lifestyle change; fixedYears is established history.
    reversibleYears,
    fixedYears,
    top3Aging,
    top3Protecting,
    categoryScores,
    // Preserve raw answers so downstream screens (Phase 2 question filtering,
    // OptionalAddOns sex-based filtering) can look up biological sex etc.
    answers,
  }
}

export function calculatePhase2Results(areaResponses, selectedAreas) {
  const responseScoreMap = {
    'Not a concern': 4,
    'Mild issue': 3,
    'Moderate issue': 2,
    'Significant concern': 1,
  }

  // Calculate area scores
  const areaScores = {}
  const recommendations = []
  const priorityFactors = []

  selectedAreas.forEach(area => {
    const responses = areaResponses[area] || []
    if (responses.length > 0) {
      const avgScore = responses.reduce((sum, r) => sum + (responseScoreMap[r] || 0), 0) / responses.length
      areaScores[area] = Math.round(avgScore) / 4 // Normalize to 0-1
    }
  })

  // Generate recommendations based on lowest scoring areas
  const sortedAreas = Object.entries(areaScores)
    .sort((a, b) => a[1] - b[1])

  sortedAreas.forEach(([area], idx) => {
    if (idx < 3) {
      priorityFactors.push(`Focus on ${formatAreaName(area)}`)
      recommendations.push(`Improve your ${formatAreaName(area)} by addressing the identified concerns`)
    }
  })

  // Add general recommendations
  recommendations.push('Schedule regular check-ups with healthcare providers')
  recommendations.push('Implement gradual lifestyle changes')
  recommendations.push('Track progress over time')

  return {
    areaScores,
    recommendations: recommendations.slice(0, 5),
    priorityFactors: priorityFactors.slice(0, 3),
  }
}

function formatAreaName(area) {
  return area
    .replace(/_/g, ' ')
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}
